import type { DriveStep } from 'driver.js';

/**
 * Guided tour for the redesigned Project Hub → Files tab.
 *
 * The tab has four views (Deliverables, Submittals, RFQs, Project folders);
 * only one is mounted at a time, so steps that live on another view carry a
 * `view` hint. The tour hook dispatches {@link PROJECT_FILES_TOUR_EVENT} so
 * `ProjectFiles` switches views before driver.js tries to highlight the target.
 */
export type ProjectFilesTourView = 'deliverables' | 'submittals' | 'rfqs' | 'folders';

/**
 * Which copy / step set to use. Mirrors the Files tab's role modes:
 * `staff` = admins, PMs and full employees (the only script that sees RFQs);
 * `engineer` = designers, engineers and scoped shop/office/field staff.
 */
export type ProjectFilesTourScript = 'staff' | 'engineer' | 'architect' | 'supplier';

/** Only full CRM staff get the RFQs view (quote status and amounts). */
export function tourScriptSeesRfqs(script: ProjectFilesTourScript): boolean {
  return script === 'staff';
}

export interface ProjectFilesTourStep extends DriveStep {
  /** View that must be active for `element` to exist. */
  view?: ProjectFilesTourView;
  /** Folder to select inside the Project folders view before highlighting. */
  folderPath?: string;
}

export const PROJECT_FILES_TOUR_SELECTORS = {
  upload: '[data-tour="pf-upload"]',
  views: '[data-tour="pf-views"]',
  structureRail: '[data-tour="pf-structure-rail"]',
  streamRow: '[data-tour="pf-stream-row"]',
  statusChips: '[data-tour="pf-status-chips"]',
  lodTimeline: '[data-tour="pf-lod-timeline"]',
  splitBanner: '[data-tour="pf-split-banner"]',
  submittals: '[data-tour="pf-submittals"]',
  rfqs: '[data-tour="pf-rfqs"]',
  folders: '[data-tour="pf-folders"]',
  designerStandards: '[data-tour="pf-designer-standards"]',
  helpBtn: '[data-tour="pf-help-btn"]',
} as const;

/** `window` CustomEvent name; `detail` is a {@link ProjectFilesTourEventDetail}. */
export const PROJECT_FILES_TOUR_EVENT = 'lctf:projectFilesTour';

export type ProjectFilesTourEventDetail =
  | { type: 'start' }
  | { type: 'setView'; view: ProjectFilesTourView; folderPath?: string }
  | { type: 'end' };

export function dispatchProjectFilesTourEvent(detail: ProjectFilesTourEventDetail): void {
  if (typeof window === 'undefined') return;
  try {
    window.dispatchEvent(new CustomEvent<ProjectFilesTourEventDetail>(PROJECT_FILES_TOUR_EVENT, { detail }));
  } catch {
    /* ignore */
  }
}

/**
 * Maps the Files tab's role flags to a tour script.
 * `staff` = admins, PMs and full employees (upload/release, RFQs, Designer Standards);
 * designers, engineers and scoped staff share the layout minus RFQs and Designer Standards management.
 */
export function resolveProjectFilesTourScript(opts: {
  supplierReadOnly?: boolean;
  architectReadOnly?: boolean;
  canManageDesignerStandards?: boolean;
}): ProjectFilesTourScript {
  if (opts.supplierReadOnly) return 'supplier';
  if (opts.architectReadOnly) return 'architect';
  return opts.canManageDesignerStandards ? 'staff' : 'engineer';
}

/** Deps injected so the builder is testable without a DOM. */
export interface BuildProjectFilesTourOptions {
  script: ProjectFilesTourScript;
  /** Checks whether an element is on the page right now (defaults to `document.querySelector`). */
  exists?: (selector: string) => boolean;
  /** When true the "Classic files view" toggle is on; multi-view steps are skipped. */
  classicView?: boolean;
}

function defaultExists(selector: string): boolean {
  return typeof document !== 'undefined' && !!document.querySelector(selector);
}

export function buildProjectFilesTourSteps(opts: BuildProjectFilesTourOptions): ProjectFilesTourStep[] {
  const { script, classicView = false } = opts;
  const exists = opts.exists ?? defaultExists;
  const S = PROJECT_FILES_TOUR_SELECTORS;
  const steps: ProjectFilesTourStep[] = [];

  const intro: Record<ProjectFilesTourScript, string> = {
    staff:
      'Files are now organised around how a job actually moves: each structure has its own file streams that advance through levels of detail at their own pace. What goes to the client lives under Submittals, what goes to shops lives under RFQs, and everything else has a home in Project folders. Nothing already staged or released has changed.',
    engineer:
      'Files are organised around structures and their file streams, each advancing through levels of detail at its own pace. Client releases sit under Submittals and supporting documents in Project folders.',
    architect:
      'Files are organised around structures and their file streams. Each stream shows the current level of detail, and the Project folders view holds briefs, regulatory documents, photos and the current client documentation.',
    supplier:
      'Each 3D file stream here advances through levels of detail; the newest version at the top is the one to build from. Older versions stay for reference.',
  };

  steps.push({
    popover: {
      title: 'The redesigned Files tab',
      description: intro[script],
    },
  });

  const push = (step: ProjectFilesTourStep, selector?: string, requireNow = true) => {
    if (selector && requireNow && !exists(selector)) return;
    steps.push(step);
  };

  push(
    {
      element: S.upload,
      popover: {
        title: 'Upload',
        description:
          'One button for everything. Add a version to an existing stream, start a new stream (and tag it to a structure), or drop a general file straight into a project folder. Drag & drop anywhere on the tab works too.',
        side: 'bottom',
        align: 'start',
      },
    },
    S.upload,
  );

  if (!classicView) {
    push(
      {
        element: S.views,
        popover: {
          title: 'One set of files, several views',
          description:
            script === 'architect'
              ? 'Deliverables shows the live file streams; Project folders holds the supporting documents. Your last view is remembered per project.'
              : tourScriptSeesRfqs(script)
                ? 'Deliverables is the working view. Submittals and RFQs are read-only lenses on what has already been released — they are built from existing releases, so nothing is duplicated. Project folders is the tidy home for everything that is not versioned. Your last view is remembered per project.'
                : 'Deliverables is the working view. Submittals is a read-only lens on what has already gone to the client — built from existing releases, so nothing is duplicated. Project folders is the tidy home for everything that is not versioned. Your last view is remembered per project.',
          side: 'bottom',
          align: 'start',
        },
      },
      S.views,
    );
  }

  push(
    {
      element: S.structureRail,
      view: 'deliverables',
      popover: {
        title: 'Streams, grouped by structure',
        description:
          script === 'supplier'
            ? 'Every stream for your scope is listed here. When a job has several structures, each structure group gets its own section so a barn file never gets mixed up with a porch file. Select one to see its version history.'
            : script === 'architect'
              ? 'Once a job has split, each structure group gets its own section — a barn at Joinery and a pavilion still at Conceptual never get mixed up. The whole-project originals are no longer listed; their versions sit at the base of every structure stream.'
              : 'Once a job has split, each structure group gets its own section — a barn at Joinery and a pavilion still at Conceptual never get mixed up. The whole-project originals are no longer listed; their versions sit at the base of every structure stream (hover "Deliverables" for the split history). The structure breakdown unlocks once a timber list is in and the proposal lists structures; before that, tracks are simply named. Once the timber lists are in, admins and PMs use "Set up structures…" to create the proposal structures from those lists and split the other whole-project tracks under them in one pass, and "Select versions" on a track to move files onto a structure track. After the split, "Add structure…" on this heading creates another proposal structure as its own group or inside a group that already exists.',
        side: 'right',
        align: 'start',
      },
    },
    S.structureRail,
  );

  if (!classicView && (script === 'staff' || script === 'engineer')) {
    push(
      {
        element: S.splitBanner,
        view: 'deliverables',
        popover: {
          title: 'Set up structures — one pass',
          description:
            'When the timber lists land, admins and PMs run "Set up structures…" once: step 1 creates the proposal structures from the timber-list CSVs (one per list, or one per Building when a CSV covers several), step 2 splits every other whole-project track — 2D, 3D, HTML — into structure groups (a single structure, or a set such as "Barn + Porch"). Each group inherits the same versions (nothing is copied; numbering carries on). The originals become read-only roots and drop out of this list; hover "Deliverables" to see when and how it happened. Later, "Add structure…" on the Deliverables heading adds another structure without splitting the job again.',
          side: 'bottom',
          align: 'start',
        },
      },
      S.splitBanner,
    );
  }

  push(
    {
      element: S.statusChips,
      view: 'deliverables',
      popover: {
        title: 'Status at a glance',
        description: tourScriptSeesRfqs(script)
          ? 'Hover the HTML, 2D, or other type badge to see the level of detail of the newest version, whether it is on the client portal or the portal is behind, and how many versions are staged for an RFQ. The line under the name shows who uploaded it and when.'
          : 'Hover the HTML, 2D, or other type badge to see the level of detail of the newest version, and whether it is on the client portal or the portal is behind. The line under the name shows who uploaded it and when.',
        side: 'right',
        align: 'start',
      },
    },
    S.statusChips,
  );

  push(
    {
      element: S.lodTimeline,
      view: 'deliverables',
      popover: {
        title: 'Version history & audit trail',
        description:
          script === 'supplier'
            ? 'Every version of the selected stream, newest first, grouped by level of detail. Download the current one from the top of the list.'
            : tourScriptSeesRfqs(script)
              ? 'The selected stream, newest version first — grouped by level of detail, or flip to Timeline for one flat history. Each version records who uploaded it, when, and every release it has been part of. Releasing to the portal or staging for an RFQ happens from here — exactly as before. When you release to the client you also pick the design phase (Concept or Engineering) and whether the client needs to approve it; that is what drives their approval trail.'
              : 'The selected stream, newest version first — grouped by level of detail, or flip to Timeline for one flat history. Each version records who uploaded it, when, and every release it has been part of, so you can always see which version the client or the office is working from.',
        side: 'left',
        align: 'start',
      },
    },
    S.lodTimeline,
  );

  if (!classicView && (script === 'staff' || script === 'engineer')) {
    steps.push({
      element: S.submittals,
      view: 'submittals',
      popover: {
        title: 'Submittals — what the client has',
        description:
          'Every file released to the client portal, grouped by design phase (Concept / Engineering), with a lane per structure once a job has split. Chips show "On portal", "Awaiting approval" or "Client approved" per release, and "Replaced" when a newer release superseded an unapproved one. Open the source version or grab the exact client copy.',
        side: 'top',
        align: 'start',
      },
    });
  }

  if (!classicView && tourScriptSeesRfqs(script)) {
    steps.push({
      element: S.rfqs,
      view: 'rfqs',
      popover: {
        title: 'RFQs — what the shops have',
        description:
          'Admins, PMs and full employees only. Every file staged or sent to providers — including packages pushed straight from the proposal — grouped by the structures each quote request covered (one group per RFQ scope, e.g. "Ext. Accents" and "everything else"), then by supplier. Once a job has structure scopes, "Whole project" starts folded. Each entry shows the version, work type, the structures it covers, where the supplier stands (Sent, Quoted, Approved or Not bidding), the quote amount, and the proposal version it was quoted against. "Open in Suppliers" jumps to the package.',
        side: 'top',
        align: 'start',
      },
    });
  }

  if (!classicView && script !== 'supplier') {
    steps.push({
      element: S.folders,
      view: 'folders',
      popover: {
        title: 'Project folders',
        description:
          script === 'architect'
            ? 'Every project gets the same tree: Admin (Project Brief, Regulatory), Reference (Photos), Client Submittals (2D Documentation, 3D Files) and Designer Standards. Files uploaded before this redesign appear under Unsorted, which only shows while it has files.'
            : 'Every project gets the same tree: Admin (Project Brief, Regulatory), Reference (Photos), Client Submittals (2D Documentation, 3D Files) and Designer Standards. Files uploaded before this redesign appear under Unsorted — move them into a folder from the row menu; Unsorted disappears once it is empty.',
        side: 'top',
        align: 'start',
      },
    });
    steps.push({
      element: S.designerStandards,
      view: 'folders',
      folderPath: 'designer-standards',
      popover: {
        title: 'Designer Standards',
        description:
          script === 'staff'
            ? 'SOPs, the .2d and .3d templates and the timber-list export preset, identical on every project. Manage the library once in Settings → Project Defaults → Designer Standards; it updates everywhere.'
            : 'SOPs, the .2d and .3d templates and the timber-list export preset — the same library on every project, kept current by the office.',
        side: 'right',
        align: 'start',
      },
    });
  }

  push(
    {
      element: S.helpBtn,
      popover: {
        title: 'Need a refresher?',
        description: 'Open this help button anytime for a summary of the views, or to replay this walkthrough.',
        side: 'bottom',
        align: 'end',
      },
    },
    S.helpBtn,
  );

  return steps;
}

// ─── First-visit persistence ────────────────────────────────────────────────

// v1: initial Files redesign (Deliverables / Submittals / RFQs / Project folders).
// RFQs are shown to the `staff` script only; the key is per script so a role
// change (e.g. promotion to full employee) replays the walkthrough.
const STORAGE_PREFIX = 'lctf:projectFilesTour:v1';

export function projectFilesTourStorageKey(uid: string | undefined, script: ProjectFilesTourScript): string {
  return `${STORAGE_PREFIX}:${script}:${uid || 'anon'}`;
}

export function hasSeenProjectFilesTour(uid: string | undefined, script: ProjectFilesTourScript): boolean {
  try {
    return !!localStorage.getItem(projectFilesTourStorageKey(uid, script));
  } catch {
    return false;
  }
}

export function markProjectFilesTourSeen(uid: string | undefined, script: ProjectFilesTourScript): void {
  try {
    localStorage.setItem(projectFilesTourStorageKey(uid, script), new Date().toISOString());
  } catch {
    /* ignore */
  }
}

/** Clears the seen flag for every script for this user (Settings → "Reset Files walkthrough"). */
export function resetProjectFilesTourSeen(uid: string | undefined): void {
  try {
    const suffix = `:${uid || 'anon'}`;
    const doomed: string[] = [];
    for (let i = 0; i < localStorage.length; i += 1) {
      const key = localStorage.key(i);
      if (key && key.startsWith(STORAGE_PREFIX) && key.endsWith(suffix)) doomed.push(key);
    }
    doomed.forEach(k => localStorage.removeItem(k));
  } catch {
    /* ignore */
  }
}
